import { BleDeviceCharacteristic, CharType } from './ble.service';

/** BLE Service UUID and device name prefix */
export const SERVICE_UUID = '0000fe40-cc7a-482a-984a-7f2ed5b3e58f';
export const DEVICE_NAME_PREFIX = 'WHEERE_';

/** Write commands */
export const WRITE_CMD = {
  initCity: 0x70,
  initGateway: 0x71,
  runtimeData: 0x72,
  toState: 0x73,
  debugLevel: 0x74,
  confAlgo: 0x75,
  getDescription: 0x76,
  writeConfig: 0x77,
  ping: 0xa0,
  reset: 0x60,
};

export const DEVICE_CHARACTERISTICS_RECORD: Record<string, BleDeviceCharacteristic> = {
  // 0000fe41 => WRITE
  WRITE: {
    name: 'WRITE',
    uuid: '0000fe41-8e22-4541-9d4c-21edae82ed19',
    type: CharType.WRITE,
  },
  // 0000fe42 => NOTIFY (POS)
  NOTIF_POS: {
    name: 'NOTIF_POS',
    uuid: '0000fe42-8e22-4541-9d4c-21edae82ed19',
    type: CharType.NOTIFY,
  },
  // 0000fe43 => NOTIFY (LOG)
  NOTIF_LOG: {
    name: 'NOTIF_LOG',
    uuid: '0000fe43-8e22-4541-9d4c-21edae82ed19',
    type: CharType.NOTIFY,
  },
  // 0000fe46 => NOTIFY (ACK)
  NOTIF_ACK: {
    name: 'NOTIF_ACK',
    uuid: '0000fe46-8e22-4541-9d4c-21edae82ed19',
    type: CharType.NOTIFY,
  },
  // 0000fe44 => NOTIFY (IQW)
  NOTIF_IQW: { name: 'NOTIF_IQW', uuid: '0000fe44-8e22-4541-9d4c-21edae82ed19', type: CharType.NOTIFY },
};

/** Tracker data interface */
export interface TrackerData {
  lat: number;
  long: number;
  alt: number;
  charging: boolean;
  soc: number;
}

/** Gateway data interface */
export interface GatewayData {
  freq_theta_calib: [number, number, [number, number]][];
  gps: [number, number, number];
  alt: number;
}

/** Interface for decapsulated data */
export interface DecapsulatedData {
  command: number;
  payload: DataView;
}

/** Interface describing the parsed position data fields */
export interface ParsedPositionData {
  lat: number;
  lon: number;
  alt: number;
  x: number;
  y: number;
  z: number;
  soc: number;
  charging: boolean;
  gps_lat: number;
  gps_lon: number;
  gps_alt: number;
  gps_hdop: number;
  gsm_connected: boolean;
}

/**
 * Decapsulates an encapsulated frame and verifies the CRC (if present).
 *
 * The frame format is:
 *   - 1 byte padding: 0xA5
 *   - 2 bytes size (little endian; first byte = LSB, second = MSB) representing (command + payload) length
 *   - 1 byte command
 *   - Payload (if any, length = size - 1)
 *   - 2 bytes CRC (if used; if CRC equals 0, it is considered not present)
 *
 * @param dataView The DataView containing the received frame.
 * @returns An object with the command and the payload as a DataView.
 * @throws An error if the padding is invalid or if CRC verification (when present) fails.
 */
export function decapsulateData(dataView: DataView): DecapsulatedData {
  const dv = dataView;
  const frameBuffer = dataView.buffer;
  let offset = 0;

  // 1. Verify the padding (1 byte)
  const padding = dv.getUint8(offset);
  if (padding !== 0xa5) {
    throw new Error('Decapsulation: Invalid padding (expected 0xA5).');
  }
  offset += 1;

  // 2. Read the size (2 bytes, little endian: first byte = LSB, second = MSB)
  const size = dv.getUint16(offset, false);
  offset += 2;

  // 3. Read the command (1 byte)
  const command = dv.getUint8(offset);
  offset += 1;

  // 4. The payload occupies (size - 1) bytes.
  const payloadLength = size - 1;
  const payloadBuffer = frameBuffer.slice(offset, offset + payloadLength);
  offset += payloadLength;

  // 5. Read the CRC (2 bytes).
  //    In our encapsulateData function, the CRC is appended as (MSB, LSB).
  //    If the read CRC equals 0, then no CRC is considered present and verification is skipped.
  const frameCrc = dv.getUint16(offset, true);
  offset += 2;
  // console.log('size', size);
  // console.log('cmd', command);
  // 6. If a non-zero CRC is present, verify it.
  if (frameCrc !== 0) {
    const crcData = new Uint8Array(frameBuffer, 0, frameBuffer.byteLength - 2);
    const computedCrc = calculateCrcCcitt(crcData, crcData.length);
    if (computedCrc !== frameCrc) {
      throw new Error(
        `Decapsulation: CRC verification failed (computed 0x${computedCrc.toString(
          16
        )} vs received 0x${frameCrc.toString(16)})`
      );
    }
  }

  return {
    command,
    payload: new DataView(payloadBuffer),
  };
}

/**
 * Extracts an ACK from the decapsulated data.
 *
 * For the initGateway command, the payload begins with a 2-byte offset,
 * followed by an ACK answer. If the ACK answer is 0 then ack = false; otherwise, true.
 *
 * @param decapsulatedData The decapsulated data.
 * @returns An object with { ack, offset, cmd } or null if invalid.
 */
export function extractAck(
  decapsulatedData: DecapsulatedData
): { ack: number; offset: number | null; cmd: number } | null {
  if (!decapsulatedData) {
    return null;
  }
  let originalFrameOffset: number | null = null;
  let offset = 0;
  const payloadLength = decapsulatedData.payload.byteLength;
  if (payloadLength <= 0) {
    return null;
  }
  // For the initGateway command, the payload begins with a 2-byte offset.
  if (decapsulatedData.command === WRITE_CMD.initGateway || decapsulatedData.command === WRITE_CMD.writeConfig) {
    originalFrameOffset = decapsulatedData.payload.getUint16(0, true);
    offset += 2;
  } else {
    originalFrameOffset = null;
  }

  const ackAnswer = decapsulatedData.payload.getUint8(offset);
  if (ackAnswer == null) {
    return null;
  }

  return { ack: ackAnswer, offset: originalFrameOffset, cmd: decapsulatedData.command };
}

/**
 * Extracts position data from a DataView.
 *
 * Expected order (each field 4 bytes, little endian):
 *   lat, lon, alt, x, y, z, status (uint32), soc (uint32),
 *   charging (uint32), gps_lat, gps_lon, gps_alt, gps_hdop.
 *
 * @param dataView The DataView containing the position data.
 * @returns A ParsedPositionData object.
 */
export function extractRuntimeTrackerData(dataView: DataView | undefined, getPrecision: any): ParsedPositionData {
  if (!dataView)
    return {
      lat: 0,
      lon: 0,
      alt: 0,
      x: 0,
      y: 0,
      z: 0,
      soc: 0,
      charging: false,
      gps_lat: 0,
      gps_lon: 0,
      gps_alt: 0,
      gps_hdop: 0,
      gsm_connected: false,
    };

  let pos = 0;
  const lat = dataView.getFloat32(pos, true);
  pos += 4;
  const lon = dataView.getFloat32(pos, true);
  pos += 4;
  const alt = dataView.getFloat32(pos, true);
  pos += 4;
  const x = dataView.getFloat32(pos, true);
  pos += 4;
  const y = dataView.getFloat32(pos, true);
  pos += 4;
  const z = dataView.getFloat32(pos, true);
  pos += 4;
  const statusNumber = dataView.getUint32(pos, true);
  pos += 4;
  const soc = dataView.getUint32(pos, true);
  pos += 4;
  const charging = dataView.getUint32(pos, true);
  pos += 4;
  const gps_lat = dataView.getFloat32(pos, true);
  pos += 4;
  const gps_lon = dataView.getFloat32(pos, true);
  pos += 4;
  const gps_alt = dataView.getFloat32(pos, true);
  pos += 4;
  const gps_hdop = dataView.getFloat32(pos, true);
  pos += 4;
  const gsm_connected = dataView.getUint8(pos);
  pos += 1;

  const chargingBool = charging === 1;
  const gsmConnectedBool = gsm_connected === 1;

  // Transformation du status en PositionPrecisionEnum

  const precisionEnum = getPrecision(statusNumber);

  return {
    lat,
    lon,
    alt,
    x,
    y,
    z,
    soc,
    charging: chargingBool,
    gps_lat,
    gps_lon,
    gps_alt,
    gps_hdop,
    gsm_connected: gsmConnectedBool,
  };
}

// /**
//  * Extracts log data from a DataView.
//  *
//  * @param data The DataView containing log data.
//  * @returns The DataView or an empty object if no data.
//  */
// export function extractLog(data: DataView | undefined): DataView | NonNullable<unknown> {
//   if (!data) return {};
//   return data;
// }

/**
 * Calculates the CRC-CCITT checksum.
 *
 * @param frame The Uint8Array representing the data.
 * @param size The number of bytes to process.
 * @returns The CRC as a number.
 */
export function calculateCrcCcitt(frame: Uint8Array, size: number): number {
  let crc = 0xffff;
  if (size === 0) {
    return 0;
  }
  for (let i = 0; i < size; i++) {
    const dbyte = frame[i];
    // Shift the byte 8 bits to the left and XOR with the CRC.
    crc ^= dbyte << 8;
    // Process each bit in the byte.
    for (let j = 0; j < 8; j++) {
      const mix = crc & 0x8000;
      crc = (crc << 1) & 0xffff;
      if (mix) {
        crc ^= 0x1021;
      }
    }
  }
  return crc;
}

/**
 * Encapsulates a command and its data into a frame and returns an ArrayBuffer.
 *
 * The frame format is:
 *   - Padding (1 byte): 0xA5
 *   - Size (2 bytes, little endian): (data length + 1 byte for command)
 *   - Command (1 byte)
 *   - Payload (if any)
 *   - CRC (2 bytes, appended in little endian order; if not used, 0 is appended)
 *
 * @param cmd The command (1 byte).
 * @param data The data to encapsulate (DataView or null).
 * @param crcUsed Indicates whether the CRC should be calculated.
 * @returns An ArrayBuffer representing the frame.
 */
export function encapsulateData(cmd: number, data: DataView | null, crcUsed: boolean): ArrayBuffer {
  const frame: number[] = [];

  // 1. Add padding: 0xA5
  frame.push(0xa5);

  // 2. Determine the size in little endian.
  //    If no data is provided, size equals 1 (only the command).
  if (data === null) {
    const size = 1;
    frame.push(size & 0xff, (size >> 8) & 0xff);
  } else {
    const size = data.byteLength + 1; // 1 byte for command + data length
    frame.push(size & 0xff, (size >> 8) & 0xff);
  }

  // 3. Add the command.
  frame.push(cmd);

  // 4. Add the data (if present).
  if (data !== null) {
    for (let i = 0; i < data.byteLength; i++) {
      frame.push(data.getUint8(i));
    }
  }

  // 5. Calculate the CRC (or use 0 if not used).
  //    The CRC is appended in little endian order (LSB is appended second).
  const crc = crcUsed ? calculateCrcCcitt(new Uint8Array(frame), frame.length) : 0;
  frame.push((crc >> 8) & 0xff, crc & 0xff);

  return new Uint8Array(frame).buffer;
}

/**
 * Construit le frame pour la commande UV_TH_CONF_DEBUGLEVEL (0x74).
 * Le payload est constitué d'1 octet qui représente la valeur du debug level.
 *
 * @param debugLevel - La valeur du debug level à envoyer.
 * @returns Un ArrayBuffer encapsulé pour la commande debug level, ou null en cas d'erreur.
 */
export function initDebugLevelFrame(debugLevel: number): ArrayBuffer | null {
  try {
    // Créer un buffer de 1 octet
    const buffer = new ArrayBuffer(1);
    const view = new DataView(buffer);
    // Écrire la valeur du debug level
    view.setUint8(0, debugLevel);
    // Encapsuler avec la commande UV_TH_CONF_DEBUGLEVEL (0x74)
    return encapsulateData(WRITE_CMD.debugLevel, view, true);
  } catch (error) {
    return null;
  }
}

/**
 * Construit le frame pour la commande UV_TH_CONF_ALGO (0x75) afin de configurer
 * les paramètres de l'algorithme.
 *
 * Le payload est constitué de :
 *   - uint32_t algo_config (4 octets)
 *   - uint8_t tk type (1 octet)
 *
 * @param algo_config - La configuration de l'algorithme (32 bits).
 * @param tkType - Le type du tracker (8 bits).
 * @returns Un ArrayBuffer encapsulé pour la commande ALGO, ou null en cas d'erreur.
 */
export function initAlgoFrame(algo_config: number, tkType: number): ArrayBuffer | null {
  try {
    // Créer un buffer de 5 octets (4 + 1)
    const buffer = new ArrayBuffer(5);
    const view = new DataView(buffer);
    // Écrire la valeur de l'algo_config (uint32_t) en little endian
    view.setUint32(0, algo_config, true);
    // Écrire la configuration du filtre IIR (uint16_t) en little endian
    view.setUint8(4, tkType);
    // Encapsuler le tout avec la commande UV_TH_CONF_ALGO (0x75)
    return encapsulateData(WRITE_CMD.confAlgo, view, true);
  } catch (error) {
    return null;
  }
}

/**
 * Construit le frame pour la commande toState (0x73) afin de définir l'état du tracker.
 *
 * @param state L'état souhaité : 1 = stdby, 3 = live, 255 = annuler la demande.
 *              Toute autre valeur sera ignorée et remplacée par 1.
 * @returns Un ArrayBuffer encapsulé pour la commande toState, ou null en cas d'erreur.
 */
export function initToStateFrame(state: 1 | 3 | 255): ArrayBuffer | null {
  try {
    // Seuls 1, 3 et 255 sont valides. Sinon, on choisit 1 par défaut.
    let validState = state;
    if (state !== 1 && state !== 3 && state !== 255) {
      validState = 1;
    }

    // Créer un buffer d'1 octet
    const buffer = new ArrayBuffer(1);
    const view = new DataView(buffer);
    view.setUint8(0, validState);

    // Encapsuler le payload avec la commande WRITE_CMD.toState (0x73)
    return encapsulateData(WRITE_CMD.toState, view, true);
  } catch (error) {
    return null;
  }
}

export function initResetFrame(): ArrayBuffer | null {
  try {
    // Créer un buffer de 1 octet
    const buffer = new ArrayBuffer(0);
    const view = new DataView(buffer);
    // Encapsuler le payload avec la commande WRITE_CMD.toState (0x73)
    return encapsulateData(WRITE_CMD.reset, view, true);
  } catch (error) {
    return null;
  }
}

export function constructWriteConfFrames(confString: string): { frame: ArrayBuffer; offset: number; length: number }[] {
  try {
    // 1. Convertir la chaîne en tableau d'octets (UTF-8)
    const encoder = new TextEncoder();
    const payloadBytes = encoder.encode(confString);
    const totalPayloadSize = payloadBytes.length;

    // 2. Définir les contraintes BLE
    const maxEncapsulatedTotal = 244;
    const encapsulationOverhead = 6;
    const maxPayload = maxEncapsulatedTotal - encapsulationOverhead; // 238 octets
    const baseHeaderSize = 2; // 2 octets pour l'entête de l'offset
    const extraHeaderSize = 2; // 2 octets pour la taille totale (première trame uniquement)
    const availablePerChunk = maxPayload - baseHeaderSize; // 236 octets pour les données

    // 3. Découper le tableau d'octets en sous-trames
    const dataFrames: { frame: ArrayBuffer; offset: number; length: number }[] = [];
    let currentOffset = 0;
    let chunkIndex = 0;

    while (currentOffset < totalPayloadSize) {
      const remaining = totalPayloadSize - currentOffset;
      // Ajuster la taille du header pour la première trame
      const isFirstChunk = dataFrames.length === 0;
      const headerSize = isFirstChunk ? baseHeaderSize + extraHeaderSize : baseHeaderSize;
      const thisChunkSize = Math.min(remaining, maxPayload - headerSize);

      // Créer un buffer pour la sous-trame : entête ajusté + données
      const chunkBuffer = new ArrayBuffer(headerSize + thisChunkSize);
      const chunkView = new DataView(chunkBuffer);

      // Écrire l'entête : l'offset actuel (uint16, little endian)
      chunkView.setUint16(0, currentOffset, true);

      // Ajouter `totalPayloadSize` dans la première trame
      if (isFirstChunk) {
        chunkView.setUint16(2, totalPayloadSize, true);
      }

      // Copier les données dans le buffer après l'entête
      const chunkBytes = new Uint8Array(chunkBuffer, headerSize);
      chunkBytes.set(payloadBytes.subarray(currentOffset, currentOffset + thisChunkSize));

      // 4. Encapsuler la sous-trame en utilisant la commande writeConf (0x77)
      const crcUsed = true; // On suppose que le CRC est utilisé, comme dans l'exemple précédent
      const encapsulatedFrame = encapsulateData(WRITE_CMD.writeConfig, new DataView(chunkBuffer), crcUsed);

      dataFrames.push({ frame: encapsulatedFrame, offset: currentOffset, length: totalPayloadSize });

      chunkIndex++;
      currentOffset += thisChunkSize;
    }

    return dataFrames;
  } catch (e) {
    return [];
  }
}
